import * as THREE from 'three';
import { TransformControls } from 'three/addons/controls/TransformControls.js';
import { ToolSystem } from './ToolSystem';
import { Renderer } from './Renderer';
import { EntityManager } from './Entities';
import { ScenarioManager } from './Scenario';
import { UIState } from '../ui/store';

// Tunable input constants
const HAND_TOOL_FORCE_MAGNITUDE = 150; // Tuned magnitude in Newtons (reduced from 500N for controllable log pushing)

export class InputController {
  private transformControl: TransformControls;
  private tooltipEl: HTMLDivElement;
  
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();
  private handForceActive = false;
  private arrowHelper: THREE.ArrowHelper;

  private activeWedgeId: string | null = null;
  private wedgeDriving = false;

  private boundOnKeyDown: (e: KeyboardEvent) => void;
  private boundOnPointerDown: (e: PointerEvent) => void;
  private boundOnPointerMove: (e: PointerEvent) => void;
  private boundOnPointerUp: (e: PointerEvent) => void;

  constructor(
    private scene: THREE.Scene,
    private camera: THREE.PerspectiveCamera,
    private renderer: Renderer,
    private toolSystem: ToolSystem,
    private entities: EntityManager,
    private scenario: ScenarioManager
  ) {
    // Setup Hand Tool Arrow
    this.arrowHelper = new THREE.ArrowHelper(
      new THREE.Vector3(0, -1, 0),
      new THREE.Vector3(0, 0, 0),
      1.5,
      0xffffff,
      0.3,
      0.2
    );
    this.arrowHelper.visible = false;
    this.scene.add(this.arrowHelper);
    // Setup TransformControls Gizmo
    this.transformControl = new TransformControls(this.camera, this.renderer.renderer.domElement);
    this.transformControl.setSpace('local');
    this.scene.add(this.transformControl.getHelper());

    // Disable OrbitControls when using the Gizmo
    this.transformControl.addEventListener('dragging-changed', (event) => {
      this.renderer.controls.enabled = !event.value;
      if (!event.value) {
        this.updateTooltip(false);
      }
    });

    this.transformControl.addEventListener('change', () => {
      if (this.transformControl.dragging) {
        this.updateTooltip(true);
      }
    });

    // Starts with gizmo hidden by default, so we do not attach on initialization

    // Setup DOM Tooltip
    this.tooltipEl = document.createElement('div');
    this.tooltipEl.style.position = 'absolute';
    this.tooltipEl.style.padding = '4px 8px';
    this.tooltipEl.style.background = 'rgba(0, 0, 0, 0.7)';
    this.tooltipEl.style.color = '#fff';
    this.tooltipEl.style.borderRadius = '4px';
    this.tooltipEl.style.fontFamily = 'monospace';
    this.tooltipEl.style.pointerEvents = 'none';
    this.tooltipEl.style.display = 'none';
    this.tooltipEl.style.zIndex = '1000';
    document.body.appendChild(this.tooltipEl);

    // Bind listeners
    this.boundOnKeyDown = this.onKeyDown.bind(this);
    this.boundOnPointerDown = this.onPointerDown.bind(this);
    this.boundOnPointerMove = this.onPointerMove.bind(this);
    this.boundOnPointerUp = this.onPointerUp.bind(this);

    // Keyboard Listeners
    window.addEventListener('keydown', this.boundOnKeyDown);

    // Mouse Event Listeners for Hand Tool
    const dom = this.renderer.renderer.domElement;
    dom.addEventListener('pointerdown', this.boundOnPointerDown);
    dom.addEventListener('pointermove', this.boundOnPointerMove);
    window.addEventListener('pointerup', this.boundOnPointerUp);
  }

  public dispose() {
    window.removeEventListener('keydown', this.boundOnKeyDown);
    const dom = this.renderer.renderer.domElement;
    dom.removeEventListener('pointerdown', this.boundOnPointerDown);
    dom.removeEventListener('pointermove', this.boundOnPointerMove);
    window.removeEventListener('pointerup', this.boundOnPointerUp);

    this.transformControl.detach();
    this.scene.remove(this.transformControl.getHelper());
    this.transformControl.dispose();
    
    this.scene.remove(this.arrowHelper);
    if (this.tooltipEl.parentNode) {
      this.tooltipEl.parentNode.removeChild(this.tooltipEl);
    }
  }

  public reset() {
    this.handForceActive = false;
    this.wedgeDriving = false;
    this.activeWedgeId = null;
    this.arrowHelper.visible = false;
    this.scenario.setHandForce(false);
    this.renderer.controls.enabled = true;
    this.renderer.renderer.domElement.style.cursor = 'default';
    this.tooltipEl.style.display = 'none';

    if (this.toolSystem.chainsawMesh) {
      this.transformControl.detach();
    }
  }

  private getRaycastIntersection(e: PointerEvent): THREE.Intersection | null {
    const dom = this.renderer.renderer.domElement;
    const rect = dom.getBoundingClientRect();
    this.pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    
    this.raycaster.setFromCamera(this.pointer, this.camera);
    
    const logMeshes = this.entities.getAll().filter(ent => ent.type === 'log' && ent.mesh).map(ent => ent.mesh);
    const intersects = this.raycaster.intersectObjects(logMeshes, false);
    
    if (intersects.length > 0) {
      return intersects[0];
    }
    return null;
  }

  private onPointerDown(e: PointerEvent) {
    const activeTool = UIState.getState().activeTool;
    if (activeTool !== 'hand' && activeTool !== 'chainsaw' && activeTool !== 'wedge') return;
    if (e.button !== 0) return; // Left click only

    // 1. If clicking or dragging a Gizmo handle, do nothing
    console.log('[Input] onPointerDown', { axis: this.transformControl.axis, dragging: this.transformControl.dragging });
    if (this.transformControl.axis !== null || this.transformControl.dragging) {
      console.log('[Input] Ignored click because Gizmo is being interacted with.');
      return;
    }

    // 2. Set up raycaster for coordinate checking
    const dom = this.renderer.renderer.domElement;
    const rect = dom.getBoundingClientRect();
    this.pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);

    // 3 & 4. Intersect with logs and chainsaw to find the closest hit
    const logMeshes = this.entities.getAll().filter(ent => ent.type === 'log' && ent.mesh).map(ent => ent.mesh);
    const interactables: THREE.Object3D[] = [...logMeshes];
    if (this.toolSystem.chainsawMesh) interactables.push(this.toolSystem.chainsawMesh);

    // We must use recursive = true to hit chainsaw sub-meshes, but logs are just single meshes
    const intersects = this.raycaster.intersectObjects(interactables, true);
    
    // Filter to visible only
    const validIntersects = intersects.filter(i => {
       let obj: THREE.Object3D | null = i.object;
       while (obj) {
          if (!obj.visible) return false;
          obj = obj.parent;
       }
       return true;
    });

    if (validIntersects.length > 0) {
      const firstHit = validIntersects[0];
      
      let clickedChainsaw = false;
      let clickedLog = false;
      
      let curr: THREE.Object3D | null = firstHit.object;
      while (curr) {
        if (curr === this.toolSystem.chainsawMesh) {
          clickedChainsaw = true;
          break;
        }
        if (logMeshes.includes(curr as THREE.Mesh)) {
          clickedLog = true;
          break;
        }
        curr = curr.parent;
      }

      if (clickedChainsaw) {
        console.log('[Input] Chainsaw intersects:', validIntersects.filter(i => {
          let c: THREE.Object3D | null = i.object;
          while (c) { if (c === this.toolSystem.chainsawMesh) return true; c = c.parent; }
          return false;
        }).map(i => i.object.name || i.object.type));
        
        if (UIState.getState().chainsawStuck) {
          console.log('[Input] Chainsaw is stuck, ignoring click.');
          return;
        }
        console.log('[Input] User clicked the chainsaw, attaching gizmo.');
        this.transformControl.attach(this.toolSystem.chainsawMesh!);
        return;
      } else if (clickedLog) {
        console.log('[Input] User clicked a log.');
        this.transformControl.detach();
        this.tooltipEl.style.display = 'none';

        if (activeTool === 'hand') {
          this.renderer.controls.enabled = false;
          this.handForceActive = true;
          this.renderer.renderer.domElement.style.cursor = 'grabbing';
          this.applyHandForce(e);
        } else if (activeTool === 'chainsaw') {
          if (UIState.getState().chainsawStuck) {
            console.log('[Input] Chainsaw is stuck, ignoring snap.');
            return;
          }
          console.log('[Input] Snapping to cut.');
          this.toolSystem.setThrottle(false);
          this.toolSystem.snapToCut(firstHit);
        } else if (activeTool === 'wedge') {
          console.log('[Input] Wedge tool clicked on log.');
          const kerfInfo = this.toolSystem.findClosestKerf(firstHit.point);
          if (kerfInfo) {
            const wedgeId = `w_${kerfInfo.kerfIndex}_${kerfInfo.radialPosition}`;
            this.activeWedgeId = wedgeId;
            this.wedgeDriving = true;
            this.renderer.controls.enabled = false;
            this.renderer.renderer.domElement.style.cursor = 'crosshair';

            this.scenario.sendWedgeCommand({
              type: 'SET_WEDGE',
              action: 'start',
              id: wedgeId,
              kerfIndex: kerfInfo.kerfIndex,
              radialPosition: kerfInfo.radialPosition
            });
          }
        }
        return;
      }
    }

    // 5. Clicked away from chainsaw & logs: detach/hide the gizmo
    console.log('[Input] User clicked away, detaching gizmo.');
    this.transformControl.detach();
    this.tooltipEl.style.display = 'none';
  }

  private onPointerMove(e: PointerEvent) {
    if (this.handForceActive) {
      this.applyHandForce(e);
    }
  }

  private applyHandForce(e: PointerEvent) {
    const intersection = this.getRaycastIntersection(e);
    if (intersection) {
       // Force direction is from camera through the point (pushing into the scene)
       const dir = new THREE.Vector3().subVectors(intersection.point, this.camera.position).normalize();
       
       this.arrowHelper.position.copy(intersection.point);
       this.arrowHelper.setDirection(dir);
       this.arrowHelper.visible = true;
       
       this.scenario.setHandForce(true, intersection.point, dir, HAND_TOOL_FORCE_MAGNITUDE);
    } else {
       // Dragged off the log, pause force but keep tool active
       this.arrowHelper.visible = false;
       this.scenario.setHandForce(false);
    }
  }

  private onPointerUp(e: PointerEvent) {
    if (this.handForceActive) {
      this.handForceActive = false;
      this.renderer.controls.enabled = true;
      this.renderer.renderer.domElement.style.cursor = 'grab';
      this.arrowHelper.visible = false;
      this.scenario.setHandForce(false);
    }

    if (this.wedgeDriving && this.activeWedgeId) {
      this.scenario.sendWedgeCommand({
        type: 'SET_WEDGE',
        action: 'stop',
        id: this.activeWedgeId
      });
      this.wedgeDriving = false;
      this.activeWedgeId = null;
      this.renderer.controls.enabled = true;
      this.renderer.renderer.domElement.style.cursor = 'crosshair';
    }
  }

  private updateTooltip(visible: boolean) {
    if (!visible || !this.toolSystem.chainsawMesh) {
      this.tooltipEl.style.display = 'none';
      return;
    }

    this.tooltipEl.style.display = 'block';
    
    // Project 3D position to 2D screen
    const pos = this.toolSystem.chainsawMesh.position.clone();
    pos.project(this.camera);

    const x = (pos.x * .5 + .5) * window.innerWidth;
    const y = (pos.y * -.5 + .5) * window.innerHeight;

    this.tooltipEl.style.left = `${x + 20}px`;
    this.tooltipEl.style.top = `${y - 20}px`;

    // Format display string based on current Gizmo mode
    if (this.transformControl.mode === 'translate') {
      const { x, y, z } = this.toolSystem.chainsawMesh.position;
      this.tooltipEl.innerText = `Pos: [${x.toFixed(2)}, ${y.toFixed(2)}, ${z.toFixed(2)}]`;
    } else {
      const { x, y, z } = this.toolSystem.chainsawMesh.rotation;
      // Convert to degrees
      const rx = (x * 180 / Math.PI).toFixed(0);
      const ry = (y * 180 / Math.PI).toFixed(0);
      const rz = (z * 180 / Math.PI).toFixed(0);
      this.tooltipEl.innerText = `Rot: [${rx}°, ${ry}°, ${rz}°]`;
    }
  }

  private onKeyDown(e: KeyboardEvent) {
    // Ignore input fields (but allow keyboard events if a checkbox/radio or range slider is focused)
    if (e.target instanceof HTMLInputElement) {
      const type = e.target.type.toLowerCase();
      if (['text', 'number', 'password', 'search', 'email', 'tel', 'url'].includes(type)) {
        return;
      }
    }
    if (e.target instanceof HTMLTextAreaElement) return;

    const saw = this.toolSystem.chainsawMesh;
    if (!saw) return;

    const stuck = UIState.getState().chainsawStuck;
    const step = 0.1;
    const angleStep = Math.PI / 16;

    switch (e.key.toLowerCase()) {
      // Translation
      case 'w': if (!stuck) saw.translateY(step); break;
      case 's': if (!stuck) saw.translateY(-step); break;
      case 'a': if (!stuck) saw.translateX(-step); break;
      case 'd': if (!stuck) saw.translateX(step); break;
      
      // Rotation
      case 'q': if (!stuck) saw.rotateY(angleStep); break;
      case 'e': if (!stuck) saw.rotateY(-angleStep); break;
      case 'z': if (!stuck) saw.rotateX(angleStep); break;
      case 'c': if (!stuck) saw.rotateX(-angleStep); break;

      // Throttle
      case 't': 
        if (!stuck) this.toolSystem.setThrottle(!this.toolSystem.throttleOn);
        break;

      // Reset
      case 'r':
        if (stuck) {
          UIState.update({ chainsawStuck: false, lastResetTime: performance.now() });
          UIState.updateSimulation('currentPressure', 0);
        }
        this.toolSystem.resetPosition();
        this.transformControl.detach();
        // Send immediate updated position so worker drops pressure next tick
        const guidebarData = this.toolSystem.getGuidebarTransform();
        if (guidebarData) {
          this.scenario.sendGuidebarState(true, guidebarData.pos, guidebarData.quat, guidebarData.scale);
        }
        break;

      // Gizmo Toggle
      case 'm':
        this.transformControl.setMode(this.transformControl.mode === 'translate' ? 'rotate' : 'translate');
        break;

      case 'escape':
        this.transformControl.detach();
        this.tooltipEl.style.display = 'none';
        break;

      case 'arrowright':
      case 'arrowleft':
        this.toolSystem.quickFlip();
        break;
      
      case 'arrowup':
        this.toolSystem.snapTop();
        break;
      
      case 'arrowdown':
        this.toolSystem.snapBottom();
        break;
    }
  }

  public forceDetachGizmo() {
    this.transformControl.detach();
    this.tooltipEl.style.display = 'none';
  }
}
